// English dashboard copy. Typed against the German key set, so a key added to
// one side without the other fails `tsc`. Number and date formats stay Swiss
// (brand rule, not a language); product names and mode / tenant ids stay
// verbatim.

import type { de } from "./de";

export const en: Record<keyof typeof de, string> = {
  // --- App shell -----------------------------------------------------------
  "app.title": "Admin Dashboard",
  "app.loading": "Loading…",
  "header.window": "Window:",
  "header.partner": "Partner:",
  "header.partnerTitle": "Scope every panel to one partner",
  "header.partnerAll": "All",
  "header.partnerGroup": "Partners",
  "header.bucketGroup": "Unassigned",
  "header.includeTest": "Include test sessions",
  "header.signOut": "Sign out",
  "header.language": "Language",
  "header.glossary": "Glossary: terms explained",
  "header.status": "Services: {status} — details under Overview › Health",
  "lang.de": "German",
  "lang.en": "English",
  "window.90d": "3 mo",
  "window.180d": "6 mo",
  "window.365d": "Max",
  "staleness.lastRefresh": "Last refresh: {when}",
  "staleness.never": "never",
  "staleness.refresh": "↻ Refresh",
  "relative.seconds": "{n}s ago",
  "relative.minutes": "{n}m ago",
  "relative.hours": "{n}h ago",
  "relative.days": "{n}d ago",
  "duration.sec": "s",
  "duration.min": "min",
  "duration.hours": "h",

  // --- Shared words --------------------------------------------------------
  "common.loading": "Loading…",
  "common.on": "on",
  "common.off": "off",
  "common.yes": "yes",
  "common.no": "no",
  "common.count": "Count",
  "common.mode": "Mode",
  "common.model": "Model",
  "common.turns": "Turns",
  "common.sessions": "Sessions",
  "common.when": "When",
  "common.session": "Session",
  "common.type": "Type",
  "common.metadata": "Metadata",
  "common.reason": "Reason",
  "common.total": "Total",
  "common.inputTokens": "Input tokens",
  "common.outputTokens": "Output tokens",
  "common.cost": "Cost",
  "common.openInInspector": "Open in the Session Inspector",
  "common.ratingPositive": "Positive",
  "common.ratingNegative": "Negative",

  // --- Sidebar sections and their tabs --------------------------------------
  "nav.sections": "Sections",
  "section.overview": "Overview",
  "section.conversations": "Conversations",
  "section.usage": "Usage",
  "section.quality": "Quality",
  "section.cost": "Cost",
  "section.ops": "Operations",
  "view.health": "Health",
  "view.ops": "Switches & budgets",
  "view.customers": "Customers & domains",
  "view.testpage": "Test page",
  "view.kpi": "Success / KPIs",
  "view.pulse": "Live Pulse",
  "view.engagement": "Engagement & Topics",
  "view.peak": "Peak Times",
  "view.quality": "Quality & RAG",
  "view.signals": "Signals & Safety",
  "view.questions": "Question check",
  "view.talentgeo": "Talent Geography",
  "view.cost": "Cost & Estimates",
  "view.estimate": "Cost per person & scenarios",
  "view.partner": "Partner Report",
  "view.aggregates": "Weekly Aggregates",
  "view.qna": "Recent Q&A",
  "view.sessions": "Session Inspector",
  "view.events": "Events Stream",
  "view.analyst": "Analysis chat",
  "view.insights": "Daily report",
  "view.prompts": "System Prompts",
  "view.config": "Config",
  "view.glossary": "Glossary",

  // --- Chat modes / partner buckets (display labels) -----------------------
  "mode.faq": "FAQ",
  "mode.profession": "Profession",
  "mode.discovery": "Discovery",
  "mode.trainer": "Skills trainer",
  "mode.multicheck": "Multicheck",
  "tenant.unknown": "No origin",
  "tenant.unmapped": "Unassigned",
  "tenant.all": "All",

  // --- Login ---------------------------------------------------------------
  "login.title": "Admin Dashboard",
  "login.thisAccount": "This account",
  "login.forbiddenSuffix": " is signed in but not authorized for the dashboard.",
  "login.forbiddenHint":
    "Please ask your gateway.one administrator for access to the dashboard.",
  "login.signInMsg": "Sign in with your gateway.one Google account.",
  "login.signInGoogle": "Sign in with Google",

  // --- Health & Awareness --------------------------------------------------
  "health.dependencies": "Dependencies",
  "health.dependenciesScope":
    "All partners, last 60 minutes — the partner filter does not apply to this row.",
  "health.backendUnreachable":
    "Backend unreachable — check uvicorn on 127.0.0.1:8080 and VITE_ADMIN_TOKEN.",
  "health.overall": "Overall:",
  "health.version": "version {v}",
  "health.northStar": "North Star",
  "health.turnsInWindow": "Turns in window",
  "health.engagedSessions": "Engaged sessions",
  "health.engagedSub": "≥3 turns",
  "health.feedbackRatio": "Feedback ratio",
  "health.ratings": "{n} ratings",
  "health.anomalies": "Anomalies",
  "health.allClear": "✅ Everything inside thresholds for the last {window}.",
  "health.dep.latency": "Latency {ms} ms",
  "health.dep.last": "last {when}",
  "dep.firestore": "Firestore",
  "dep.geoip": "GeoIP",
  "dep.anthropic_vertex": "Claude (Vertex AI)",
  "dep.gemini_vertex": "Gemini (Vertex AI)",
  "dep.vertex_search": "RAG search (Vertex AI Search)",
  "dep.edge_geo_header": "Edge geo header (Cloud Armor)",
  "status.ok": "ok",
  "status.degraded": "degraded",
  "status.down": "down",
  "status.unknown": "unknown",
  "anomaly.no_answer_rate": "No-answer rate",
  "anomaly.mid_stream_fail_rate": "Mid-stream failures",
  "anomaly.claude_cache_hit_rate": "Cache hit rate (Claude)",
  "anomaly.rag_chunk_sanitized": "RAG content sanitized",
  "anomaly.guardrail_blocked": "Guardrail triggered",
  "anomaly.error_rate": "Error rate",
  "severity.info": "info",
  "severity.warning": "warning",
  "severity.critical": "critical",

  // --- Live Pulse ----------------------------------------------------------
  "pulse.active5m": "Active 5 min",
  "pulse.active1h": "Active 1 h",
  "pulse.active24h": "Active 24 h",
  "pulse.turnsSub": "turns: {n}",
  "pulse.avgQuestions": "avg {n} questions/session",
  "pulse.modeKickoff": "Mode kickoff & journey",
  "pulse.chart.modeKickoff": "Mode kickoff",
  "pulse.topTransitions": "Top transitions",
  "pulse.from": "From",
  "pulse.to": "To",
  "pulse.noTransitions": "No mode transitions in this window.",
  "pulse.origins": "Origins",
  "pulse.embeddingHost": "Embedding host",
  "pulse.noOrigins": "No origins recorded yet.",
  "pulse.audience": "Audience",
  "pulse.chart.locale": "Locale",
  "pulse.chart.device": "Device",
  "pulse.chart.country": "Country",

  // --- Success / KPIs ------------------------------------------------------
  "kpi.howToRead": "How to read these numbers",
  "kpi.howToReadText":
    "These are the actual values from the data available server-side — no target thresholds, no traffic-light pass/fail. The denominator is the number of sessions. A session starts when someone chooses a mode or writes the first message after the consent. What we cannot see: someone who loads or opens the widget and leaves creates no session — that is visible only in GA4. Several gateway.one subpages do not count more than once: the same browser keeps using the same session on the same day.",
  "kpi.funnel": "Conversion funnel",
  "kpi.funnelText":
    "From a started to a deep conversation. Percentages are relative to all sessions{total}.",
  "kpi.privacyWall": "Privacy wall",
  "kpi.privacyWallText":
    "A session starts only after the consent. Someone who stops at the privacy notice therefore leaves no session; only GA4 shows it. The values here come from older sessions created before the consent, and they go towards zero.",
  "kpi.shown": "Notice shown",
  "kpi.shownSub": "sessions on the first-consent path",
  "kpi.accepted": "Accepted",
  "kpi.acceptRate": "Accept rate: {rate}",
  "kpi.na": "n/a",
  "kpi.stopped": "Stopped",
  "kpi.stoppedSub": "stopped at the privacy notice (shown, not accepted)",
  "kpi.preAccepted":
    "{n} more sessions were created only after the consent (the normal case today) and are not counted here.",
  "kpi.notMeasurable": "not measurable server-side",
  "kpi.ofDenominator": "{num} of {den} {label}",
  "kpi.group.Nachfrage": "Demand",
  "kpi.group.Usability": "Usability",
  "kpi.group.Wirkung": "Impact",
  "kpi.funnel.opened": "Conversation started (session)",
  "kpi.funnel.privacy_accepted": "Privacy notice accepted",
  "kpi.funnel.messaged": "First message sent",
  "kpi.funnel.engaged": "Deep conversation (3+ questions)",
  "kpi.metric.reichweite.label": "Reach",
  "kpi.metric.reichweite.den": "page views",
  "kpi.metric.reichweite.note":
    "Not measurable server-side: someone who loads or opens the widget but starts no conversation creates no session. Page views and opens are visible only in GA4 (where subpages count per page view). Server-side we count started conversations.",
  "kpi.metric.wiederkehr.label": "Return visits",
  "kpi.metric.wiederkehr.den": "sessions",
  "kpi.metric.wiederkehr.note":
    "Approximation: sessions active across more than 6 hours — the same browser (localStorage) came back after a pause. No cross-person identifier. Lower bound: someone who clears browser storage and returns appears as a new session and is not recognisable as a return.",
  "kpi.metric.gespraechstiefe.label": "Conversation depth",
  "kpi.metric.gespraechstiefe.den": "sessions",
  "kpi.metric.gespraechstiefe.note":
    "Share of sessions with at least 3 questions (same definition as North Star and Engagement).",
  "kpi.metric.fruehabbruch.label": "Early drop-off",
  "kpi.metric.fruehabbruch.den": "sessions",
  "kpi.metric.fruehabbruch.note":
    "Share of sessions with at most 1 question (bounce). A chosen mode without a question counts; someone who stops at the privacy notice or on the start screen creates no session and is missing here.",
  "kpi.metric.funnel_klick.label": "Funnel click",
  "kpi.metric.funnel_klick.den": "sessions with a message",
  "kpi.metric.funnel_klick.note":
    "Share of sessions with at least one question in which a cited source (gateway.one / junior / wiki) was clicked — the jump from the chat into the funnel. Source: CD18 beacon.",
  "kpi.metric.hilfreich.label": "Helpfulness rate",
  "kpi.metric.hilfreich.den": "ratings given",
  "kpi.metric.hilfreich.note":
    "Share of positive among all thumbs ratings given. Voluntary — only a fraction of users rate, so the sample is small.",

  // --- Engagement & Topics -------------------------------------------------
  "engagement.turnVolume": "Turn volume & satisfaction",
  "engagement.chart.turnsPerSession": "Turns per session",
  "engagement.feedbackPerMode": "Feedback per mode",
  "engagement.ratio": "Ratio",
  "engagement.noFeedback": "No feedback yet.",
  "engagement.bounceByMode": "Bounce rate by mode",
  "engagement.bounce": "Bounce",
  "engagement.duration": "Chat duration per mode",
  "engagement.chats": "Chats",
  "engagement.multiTurn": "of which ≥2 turns",
  "engagement.avgDuration": "Avg duration",
  "engagement.median": "Median",
  "engagement.max": "Max",
  "engagement.noChats": "No chats in this window.",
  "engagement.durationNote":
    "First to last interaction per session and mode (each tab is its own chat). Avg/median over chats with ≥2 turns only — single-turn chats have no measurable span and would pull the average towards 0.",
  "engagement.discoveryStart": "Orientierung: start with questions or free text",
  "engagement.discoveryStart.start": "Start",
  "engagement.discoveryStart.questions": "Schnellstart questions",
  "engagement.discoveryStart.freeText": "Free text",
  "engagement.discoveryStart.share": "Share",
  "engagement.discoveryStart.turnsPer": "Avg turns per conversation",
  "engagement.discoveryStart.none": "No Orientierung conversations in this window.",
  "engagement.topics": "What are people asking about?",
  "engagement.chart.intent": "Question intent",
  "engagement.chart.sessionIntent": "What conversations are about",
  "engagement.chart.mentions": "Keywords mentioned",
  "engagement.chart.berufsfeld": "Occupational fields",
  "engagement.chart.affect": "Affect",
  "engagement.replyTurns": "{n} replies to the chatbot's questions not counted",
  "engagement.replyTurnsOne": "1 reply to a question of the chatbot not counted",
  "engagement.topicsNote":
    "Question intent, keywords and occupational fields come from regex classifiers (core/classify.py), roughly 30 to 50 % recall on free-form teen phrasing: read them as a pattern, not as per-turn truth. «What conversations are about» is set by the title model.",
  "engagement.terms": "Terms asked about",
  "engagement.terms.baseline": "Window before ({window})",
  "engagement.terms.noCompare":
    "No comparison: twice the window reaches past the look-back, or the read hit its limit.",
  "engagement.terms.none": "No terms in this window.",
  "engagement.professionTopics": "Profession topics",
  "engagement.chart.professionTopics": "Conversations per topic",
  "engagement.professionTopics.conversations": "{n} Profession conversations in this window",
  "engagement.professionTopics.none": "No Profession conversations in this window.",
  "professionTopic.alltag": "Daily work",
  "professionTopic.ausbildung": "Training",
  "professionTopic.anforderungen": "Requirements",
  "professionTopic.lohn": "Pay",
  "professionTopic.arbeitsfelder": "Fields of work",
  "professionTopic.weiterbildung": "Further training",
  "professionTopic.spezialisierungen": "Specialisations",
  "professionTopic.sonnenseiten": "Pros and cons",
  "professionTopic.zukunft": "Future and AI",
  "engagement.affect": "Affect signals",
  "engagement.places": "Places named (B2B analytics)",
  "engagement.places.none": "No places in this window.",
  "engagement.interestVsChoice": "Interest vs choice",
  "engagement.interestTag": "Interest",
  "engagement.resolvedBerufsfeld": "Resolved occupational field",
  "engagement.noGap": "No interest:* trait + occupational-field pairs in this window.",
  "engagement.feedbackComments": "Latest feedback comments",
  "engagement.comment": "Comment",
  "engagement.noComments": "No feedback comments yet.",

  // --- The backend's vocabulary (core/classify.py, geo/places.py) ----
  "intent.multicheck": "Multicheck®",
  "intent.basic_check": "Basic Check®",
  "intent.wayfi": "WayFi®",
  "intent.identyfi": "IdentyFi®",
  "intent.berufskompass": "Berufskompass",
  "intent.assessment": "Aptitude tests in general",
  "intent.deadline": "Dates & deadlines",
  "intent.schnupperlehre": "Taster apprenticeship",
  "intent.lehrstelle": "Finding an apprenticeship",
  "intent.interview": "Job interview",
  "intent.application": "Application",
  "intent.salary": "Pay",
  "intent.product_cost": "Price & cost",
  "intent.duration": "Duration",
  "intent.requirements": "Requirements",
  "intent.further_education": "Further education",
  "intent.daily_work": "Daily work",
  "intent.profession_overview": "Profession overview",
  "intent.location": "Place & commute",
  "intent.employer": "Training companies",
  "intent.privacy": "Data protection",
  "intent.platform": "About gateway.one",
  "intent.meta_bot": "About the chatbot",
  "intent.support": "Problem or how to use it",
  "intent.comparative": "Comparison",
  "intent.decisional": "Help deciding",
  "intent.procedural": "How to",
  "intent.definitional": "Definition",
  "intent.constraint": "Constraint stated",
  "intent.setback": "Setback reported",
  "intent.rejection": "Pushback on the chatbot",
  "intent.affirmation": "Agreement, tell me more",
  "intent.smalltalk": "Small talk",
  "intent.other": "Other",
  "affect.positive": "Positive",
  "affect.frustrated": "Frustrated",
  "affect.confused": "Confused",
  "affect.urgent": "Urgent",
  "affect.uncertain": "Unsure",
  "affect.profanity": "Profanity",
  "place.home": "Home towns",
  "place.target": "Search areas",
  "place.mention": "Mentioned",
  "place.col.conversations": "Conversations",
  "trend.new": "new",
  "trend.rising": "rising",
  "trend.falling": "falling",
  "terms.col.term": "Term",
  "terms.col.conversations": "Conversations",
  "terms.col.trend": "Trend",

  // --- Peak times ----------------------------------------------------------
  "peak.rightNow": "Right now (5 min)",
  "peak.turns": "turns",
  "peak.baseline": "Baseline 5 min",
  "peak.baselineSub": "median for this hour of the week",
  "peak.rateVsBaseline": "Rate vs baseline",
  "peak.noBaseline": "no baseline yet",
  "peak.above": "above baseline",
  "peak.within": "within baseline",
  "peak.whenSpike": "When does it spike?",
  "peak.chart.heatmap": "Hour × weekday heatmap",
  "peak.chart.hour": "Hour of day",
  "peak.chart.weekday": "Weekday",
  "peak.localeHour": "Locale × hour",
  "peak.noLocale": "No locale data yet.",
  "peak.tooltipTurns": "{day} {hour} — {n} turns",
  "weekday.short": "Mon,Tue,Wed,Thu,Fri,Sat,Sun",

  // --- Quality & RAG -------------------------------------------------------
  "quality.noAnswerRate": "No-answer rate",
  "quality.noAnswerSub": "no_answer_found + low_relevance_answer",
  "quality.streamingFailures": "Streaming failures",
  "quality.streamingSub": "streaming_completed = false",
  "quality.verifier": "Verifier (Multicheck®)",
  "quality.avgConfidence": "avg confidence: {v}",
  "quality.relevance": "RAG relevance distribution",
  "quality.chart.relevance": "Highest relevance score per answer",
  "quality.funnel": "Retrieval funnel per mode",
  "quality.col.docsFound": "Docs found",
  "quality.col.pagesForwarded": "Pages forwarded",
  "quality.col.pagesCited": "Pages cited",
  "quality.col.fillerDropped": "Filler dropped (turns)",
  "quality.col.avgContext": "Avg context chars",
  "quality.col.snippetOnly": "Snippet-only turns",
  "quality.noRetrieval": "No retrieval turns in this window.",
  "quality.topCited": "Top cited sources",
  "quality.col.url": "URL",
  "quality.col.title": "Title",
  "quality.col.sanitizer": "Sanitizer hits",
  "quality.offers": "Apprenticeships — places & professions (from cited sources)",
  "quality.offersNote":
    "Derived from the cited junior apprenticeship listings (URL + title): WHERE the openings are and WHICH professions were shown — not what the learners typed. Distinct by session.",
  "quality.topPlaces": "Top places (from cited listings)",
  "quality.place": "Place",
  "quality.noPlaces": "No apprenticeship places in this window.",
  "quality.topProfessions": "Top professions (from cited listings)",
  "quality.profession": "Profession",
  "quality.noProfessions": "No apprenticeship professions in this window.",
  "quality.contentGaps": "Content gaps — recurring queries",
  "quality.query": "Query (normalized)",
  "quality.noGaps": "No recurring queries yet.",
  "quality.guardrailReasons": "Guardrail reasons (output check hits)",
  "quality.noGuardrail": "No guardrail triggers in this window.",
  "quality.verifierIssues": "Multicheck® verifier issues",
  "quality.issue": "Issue",
  "quality.noVerifier": "No verifier issues recorded.",

  // --- Signals & Safety ----------------------------------------------------
  "signals.safety": "Safety & abuse probes",
  "signals.turnsInWindow": "Turns in window",
  "signals.offTopic": "Off-topic attempts",
  "signals.attacks": "Prompt attacks",
  "signals.offTopicByMode": "Off-topic by mode",
  "signals.attacksByMode": "Prompt attacks by mode",
  "signals.selfElimination": "Self-elimination reasons",
  "signals.selfEliminationSub": "(R7 — the stated “no”)",
  "signals.subflow": "Sub-flow signals",
  "signals.multiIntent": "Multi-intent turns",
  "signals.geoRadius": "Apprenticeship searches by commute radius",
  "signals.trainerTopics": "Skills trainer: interview topics",
  "signals.topic": "Topic",
  "signals.mcVariants": "Multicheck® variants",
  "signals.variant": "Variant",
  "signals.mcSubtests": "Multicheck® subtests",
  "signals.subtest": "Subtest",
  "signals.zeit": "Multicheck® timed training",
  "signals.timesBySubtest": "Answer times per subtest",
  "signals.timesByVariant": "Answer times per variant",
  "signals.noTimed": "No timed-training turns in this window.",
  "signals.results": "Multicheck® training results",
  "signals.accBySubtest": "Correct rate per subtest",
  "signals.accByThema": "Correct rate per task topic",
  "signals.tasks": "Tasks",
  "signals.correct": "Correct",
  "signals.firstTry": "First try",
  "signals.afterHelp": "After a hint",
  "signals.revealed": "Solution shown",
  "signals.unsolved": "Unsolved",
  "signals.skippedTasks": "Skipped",
  "signals.rate": "Rate",
  "signals.noGraded": "No graded tasks in this window.",
  "signals.legacyTitle": "Earlier records (before prompt 1.17.0)",
  "signals.legacyRow": "Single gradings without attempt history",
  "signals.record": "Record",
  "signals.coverageBounded":
    "A sample, not the whole window: the history reads are bounded.",
  "signals.coverageSessions": "sessions read",
  "signals.coverageOmitted": "omitted",
  "signals.coverageTruncated": "histories truncated",
  "signals.sessionBehaviour": "Session behaviour",
  "signals.sessionsInWindow": "Sessions in window",
  "signals.sourceClicks": "Source clicks",
  "signals.continued": "Continued after no-answer",
  "signals.topClicked": "Top clicked professions",
  "signals.modeSwitches": "Mode switches per session",
  "signals.switches": "Switches",
  "signals.noneInWindow": "None in window.",
  "signals.n": "N",
  "signals.avg": "Avg",

  // --- Talent geography ----------------------------------------------------
  "talentgeo.minN": "min_n",
  "talentgeo.minNExportable": "10 (k≥10, shareable)",
  "talentgeo.overview": "Apprenticeship searches in window",
  "talentgeo.searches": "Searches (count gate)",
  "talentgeo.withHome": "with home commune",
  "talentgeo.commuteAvail": "Transit times available",
  "talentgeo.zero": "Zero-result searches",
  "talentgeo.suppressed": "suppressed communes (min_n)",
  "talentgeo.demand": "Demand by home commune",
  "talentgeo.offers": "Offers shown by commune",
  "talentgeo.cantons": "Cantons & top professions",
  "talentgeo.demandByCanton": "Demand by canton",
  "talentgeo.canton": "Canton",
  "talentgeo.topProfessions": "Top professions (search term)",
  "talentgeo.col.commune": "Commune",
  "talentgeo.col.ct": "Ct.",
  "talentgeo.col.searches": "Searches",
  "talentgeo.col.zero": "0 results",
  "talentgeo.col.medianNearest": "Median nearest offer",
  "talentgeo.col.noOffer30": "no offer ≤30'",
  "talentgeo.col.noOffer60": "no offer ≤60'",
  "talentgeo.col.topProfessions": "Top professions",
  "talentgeo.col.impressions": "Impressions",
  "talentgeo.col.medianDistance": "Median distance to demand",
  "talentgeo.noDemand":
    "No apprenticeship search with a home commune in this window (or every row is below min_n).",
  "talentgeo.noOffers": "No shown offers with a commune in this window.",
  "talentgeo.minutes": "{n} min",
  "talentgeo.bfs": "BFS {n}",

  // --- Cost & Estimates ----------------------------------------------------
  "cost.todaySoFar": "Today so far",
  "cost.projectedEod": "projected end of day: {v}",
  "cost.last7d": "Last 7 days",
  "cost.monthlyProjection": "monthly projection: {v}",
  "cost.annualized": "Annualized",
  "cost.annualizedSub": "last 30 days × 12,17",
  "cost.incomplete":
    "Read limit reached: of this window, only the newest {n} turns are counted; one read returns no more. Totals, the daily chart and the tables come out too low. For complete figures, choose a shorter window.",
  "cost.spanIncomplete":
    "Read limit reached: of the last {days} days, only the newest {n} turns are counted. This card's figures come out too low; the window choice does not change that.",
  "cost.efficiency": "Efficiency",
  "cost.perTurn": "Cost / turn",
  "cost.perTurnSub": "{turns} turns · {perSession} per session ({sessions})",
  "cost.cacheRw": "Cache read / write",
  "cost.cacheRwSub": "{read} read / {write} written · break-even at {breakEven}",
  "cost.windowTotal": "Window total",
  "cost.windowTotalSub": "sum over all modes",
  "cost.cacheSavings": "Cache savings (Claude prompt cache)",
  "cost.cacheReads": "Cache reads",
  "cost.cacheReadsSub": "tokens served from cache",
  "cost.savedOnReads": "Saved on reads",
  "cost.savedOnReadsSub": "vs. full input rate ({naive}); cache-write premium not deducted",
  "cost.netSaving": "Net saving",
  "cost.netSavingSub": "after the {premium} cache-write premium; reads cost {actual}",
  "cost.chart.daily": "Daily",
  "cost.chart.area": "Trend",
  "cost.chart.cumulative": "Cumulative",
  "cost.chart.cumulativeTitle": "Cumulative cost in window (USD ≈ CHF)",
  "cost.chart.dailyTitle": "Cost per day (USD ≈ CHF)",
  "cost.series.input": "input",
  "cost.series.output": "output",
  "cost.series.cache_read": "cache read",
  "cost.series.cache_create": "cache write",
  "cost.series.cumulative": "cumulative",
  "cost.perTenant": "Cost per partner and mode",
  "cost.perTenantModeHint":
    "Bold rows are the partner total; beneath them the modes with the model that priced them — the line an invoice item is written on.",
  "cost.col.partner": "Partner",
  "cost.col.modeModel": "Mode / model",
  "cost.col.turns": "Turns",
  "cost.col.sessions": "Sessions",
  "cost.col.inputTok": "Input tokens",
  "cost.col.outputTok": "Output tokens",
  "cost.col.cacheRead": "Cache read",
  "cost.col.cacheCreate": "Cache write",
  "cost.col.cost": "Cost",
  "cost.col.perTurn": "Avg / turn",
  "cost.col.perSession": "Avg / session",
  "cost.col.avgTokSession": "Avg tokens/session",
  "cost.ratePer1m": "{rate} $/1M input",
  "cost.exportCsv": "⬇ CSV for billing",
  "cost.exportCsvTitle": "Download partner × mode as CSV (Excel)",
  "cost.unattributed":
    "{n} turns come from sessions that started before the window — they count under “No origin”, not under a partner.",
  "cost.perMode": "Per-mode breakdown",
  "cost.export.meta.window": "Window",
  "cost.export.meta.filter": "Partner filter",
  "cost.export.meta.generated": "Generated",
  "cost.export.meta.basis": "Basis",
  "cost.export.meta.basisNote":
    "LLM spend from stored token counts × price table (USD); title, recap and search-planning calls and infrastructure excluded — not an invoice.",
  "cost.export.total": "Total",
  "cost.export.col.partner": "Partner",
  "cost.export.col.mode": "Mode",
  "cost.export.col.model": "Model",
  "cost.export.col.turns": "Turns",
  "cost.export.col.sessions": "Sessions",
  "cost.export.col.inputTokens": "Input tokens",
  "cost.export.col.outputTokens": "Output tokens",
  "cost.export.col.cacheRead": "Cache read",
  "cost.export.col.cacheCreate": "Cache written",
  "cost.export.col.costUsd": "Cost USD",
  "cost.export.col.perTurn": "USD per turn",
  "cost.export.col.perSession": "USD per session",

  // --- Cost per person & scenarios ------------------------------------------
  "estimate.perPerson": "Cost per person (last 30 days)",
  "estimate.segment.all": "All",
  "estimate.segment.anonymous": "Anonymous (per session)",
  "estimate.segment.signed_in": "Signed in (per pseudonym)",
  "estimate.col.segment": "Group",
  "estimate.col.people": "People",
  "estimate.col.sessionsPerPerson": "Avg sessions / person",
  "estimate.col.turnsPerPerson": "Avg turns / person",
  "estimate.col.mean": "Avg cost",
  "estimate.col.p25": "P25",
  "estimate.col.median": "Median",
  "estimate.col.p90": "P90",
  "estimate.minPeople": "Percentiles and scenarios from {n} people per group.",
  "estimate.profiles": "Measured profile per mode",
  "estimate.col.inputPerTurn": "Avg input / turn",
  "estimate.col.outputPerTurn": "Avg output / turn",
  "estimate.col.cacheReadPerTurn": "Avg cache read / turn",
  "estimate.col.cacheWritePerTurn": "Avg cache write / turn",
  "estimate.col.usdPerTurn": "Avg cost / turn",
  "estimate.scenarios": "Scenario calculator",
  "estimate.peoplePerMonth": "People per month",
  "estimate.apply": "Calculate",
  "estimate.scenario.defensive": "Defensive",
  "estimate.scenario.normal": "Normal",
  "estimate.scenario.offensive": "Offensive",
  "estimate.basis.p25": "like the lightest quarter (P25)",
  "estimate.basis.mean": "like the average",
  "estimate.basis.p90": "like the heaviest 10 % (P90)",
  "estimate.perMonthSub": "per month · {v} per person, {basis}",
  "estimate.tooFewPeople": "Too few people in the last 30 days for scenarios (at least {n}).",
  "estimate.normalByMode": "Normal scenario per mode",
  "estimate.col.perPerson": "per person",
  "estimate.col.perMonth": "per month",
  "estimate.basisNote":
    "Model cost (LLM) at the price table, USD ≈ CHF; titles, recaps, search planning, search and infrastructure excluded.",
  "estimate.incomplete":
    "Read limit reached: of the last 30 days, only the newest {n} turns are counted; one read returns no more. People who chatted only earlier are missing from the counts, and signed-in people count with their recent sessions only, so their cost comes out too low. The values per anonymous person still hold: they rest on the most recent days.",
  "estimate.limits": "Who reaches a limit (last 30 days)",
  "estimate.col.limitHit": "At a limit",
  "estimate.col.limitShare": "Share",
  "estimate.limitReason.questions": "Question limit",
  "estimate.limitReason.cost": "Cost limit, anonymous",
  "estimate.limitReason.daily_limit": "Day limit, signed in",
  "estimate.limitWarning": "above {share}",
  "estimate.limitNote":
    "Up to {share} of a group at a limit is unremarkable. Above it many run into one: then the limits under Operations › Switches & budgets are worth a look. The warning applies from {n} people per group.",
  "estimate.signedInAfterLimit":
    "{n} people reached a limit without a login and signed in afterwards.",
  "estimate.caps": "Take the limits into account",
  "estimate.capOff": "unlimited",
  "estimate.capsInForce":
    "With the limits in force: anonymous {anonymous} per session, signed in {signedIn} per person and day, everyone together {daily} per day.",
  "estimate.capsMonth": "A month then costs at most {month}.",
  "estimate.budgetBound": "Bounded by the daily budget: on full days the chat pauses.",

  // --- Who chatted (peak times) ---------------------------------------------
  "activity.title": "Who chatted?",
  "activity.today": "Today so far",
  "activity.week": "Last 7 days",
  "activity.month": "Last 30 days",
  "activity.perDay": "people per day on average",
  "activity.change": "{change} against the {days} days before (avg {previous})",
  "activity.noPrevious": "No comparison: no conversations in the {days} days before",
  "activity.people": "People in the period: {n}",
  "activity.split": "{signedIn} signed in, {anonymous} anonymous",
  "activity.chart": "People per day, last 30 days",
  "activity.signedIn": "Signed in",
  "activity.anonymous": "Anonymous",
  "activity.scope":
    "Complete days up to yesterday (Zurich), independent of the window above; the partner filter applies. Counted is whoever asked at least one question.",
  "activity.incomplete":
    "More sessions than the read limit: the oldest days are incomplete, and the comparison with the month before comes out too low.",

  // --- Partner report ------------------------------------------------------
  "partner.select": "— select —",
  "partner.partner": "Partner",
  "partner.sessionsSuffix": "{n} sessions",
  "partner.noMapping":
    "No partner mapping configured yet (CHATBOT_ORIGIN_TENANT_MAP) — only the two built-in buckets are available.",
  "partner.minNWarn":
    "min_n = {n}: internal view only. Anything handed to a partner must be produced with min_n ≥ {k}.",
  "partner.pickAbove": "Select a partner above.",
  "partner.reach": "Reach",
  "partner.engagedSub": "{n} with ≥ 3 questions",
  "partner.avgTurns": "avg {n} per session",
  "partner.satisfaction": "Satisfaction",
  "partner.cost": "Cost",
  "partner.totalInWindow": "Total in window",
  "partner.perTurn": "Per turn",
  "partner.perSession": "Per session",
  "partner.noTurns": "No turns in this window.",
  "partner.quality": "Quality",
  "partner.noGoodAnswer": "Without a good answer",
  "partner.turnsN": "{n} turns",
  "partner.abortedStreams": "Aborted streams",
  "partner.abortedSub": "technical errors mid-answer",
  "partner.ratedAnswers": "Rated answers",
  "partner.ratedSub": "basis of the satisfaction rate",
  "partner.content": "Content profile",
  "partner.language": "Language",
  "partner.intent": "Question intent",
  "partner.berufsfeld": "Occupational field",
  "partner.suppressed":
    "{n} rows suppressed (below min_n = {k}) — too little traffic to report anonymously. Not “no interest”.",
  "partner.limits": "Basis & limits",
  "partner.mapping": "Mapping",
  "partner.builtinBucket": "Built-in bucket, not a configured partner.",
  "partner.hostsSeen": "Hosts seen",
  "partner.unattributedRow": "Unattributed turns",
  "partner.unattributedText":
    "{n} — from sessions that started before the window. They are missing here rather than credited to the wrong partner.",
  "partner.notIncluded": "Not included",
  "partner.notIncludedText":
    "Title, recap and search-planning calls (they create no turn) and all infrastructure cost (networking, Cloud Run, Firestore) — these cannot be attributed per partner. The figures are LLM spend, not an invoice basis.",

  // --- Weekly aggregates ---------------------------------------------------
  "aggregates.title": "Weekly aggregates",
  "aggregates.noneTitle": "No weekly aggregates yet",
  "aggregates.noneText":
    "The weekly aggregation job has not written an aggregate for a completed ISO week yet. Once it runs, demand, content gaps and occupational-field pivots appear here. Every cell is k≥10-suppressed, so low-traffic weeks stay empty by design.",
  "aggregates.demand": "Demand by occupational field",
  "aggregates.demandMeta": "{turns} turns in window · {suppressed} cell(s) suppressed below k={k}",
  "aggregates.berufsfeld": "Occupational field",
  "aggregates.locale": "Locale",
  "aggregates.turns": "Turns",
  "aggregates.noCell": "No cell reached k={k} this week.",
  "aggregates.unknown": "(unknown)",
  "aggregates.contentGaps": "Content gaps",
  "aggregates.gapsMeta": "Relevance floor {floor} · {suppressed} cell(s) suppressed below k={k}",
  "aggregates.noAnswer": "No answer",
  "aggregates.lowRelevance": "Low relevance",
  "aggregates.noGapCell": "No content-gap cell reached k={k} this week.",
  "aggregates.pivot": "Occupational-field pivots",
  "aggregates.pivotMeta":
    "{sessions} multi-field session(s) · {suppressed} cell(s) suppressed below k={k}",
  "aggregates.from": "From",
  "aggregates.to": "To",
  "aggregates.noPivot": "No pivot reached k={k} this week.",

  // --- Recent Q&A ----------------------------------------------------------
  "qna.title": "Recent questions & answers ({n})",
  "qna.col.when": "When",
  "qna.col.mode": "Mode",
  "qna.col.question": "Question",
  "qna.col.answer": "Answer",
  "qna.col.sources": "Sources",
  "qna.col.session": "Session",
  "qna.none": "No questions in this window.",

  // --- Session inspector ---------------------------------------------------
  "sessions.recent": "Recent sessions",
  "sessions.col.when": "When",
  "sessions.col.mode": "Mode",
  "sessions.col.q": "Q",
  "sessions.col.tags": "Tags",
  "sessions.noneRecent": "No sessions in the lookback window.",
  "sessions.lookup": "Lookup by session ID",
  "sessions.placeholder": "paste session_id",
  "sessions.reload": "Reload",
  "sessions.metadata": "Session metadata",
  "sessions.export": "⬇ Export Excel/CSV",
  "sessions.exportTitle":
    "Download the whole conversation (all turns + metadata) as CSV for Excel",
  "sessions.turns": "Turns ({n})",
  "sessions.widgetView": "Widget view",
  "sessions.widgetViewTitle":
    "Render answers like the widget does (markdown + SVG) to check graphics and formatting",
  "sessions.user": "User",
  "sessions.assistant": "Assistant",
  "sessions.guardrail": "Guardrail: {reason}",
  "sessions.guardrailYes": "yes",
  "sessions.midStreamFail": "mid-stream fail",
  "sessions.tokens": "tokens: {in} in / {out} out",
  "sessions.cacheHit": " · cache hit",
  "sessions.intent": "intent: {v}",
  "sessions.berufsfeld": " · occupational field: {v}",
  "sessions.retrievalTitle": "found → forwarded → cited",
  "sessions.retrievalLegend":
    "Per turn: “retrieval topic” is the profession the search was anchored on, “search query” the scrubbed text that went to Vertex with its catalogue labels, “latency” the time to the first token and to the end of the answer. Together they show why a turn cited what it cited, and what a thinking depth costs.",
  "sessions.retrievalTopic": "retrieval topic: {v}",
  "sessions.searchQuery": "search query (Vertex): “{v}”",
  "sessions.retrievalContextTitle":
    "What this turn's search was anchored on and what went to Vertex",
  "sessions.latencyTitle":
    "Time to the first token and to the end of the answer, measured server-side",
  "sessions.depthTitle":
    "Reasoning depth the model wrote this answer with: effort on Claude, thinking on Gemini. It explains this turn's answer time and cost.",
  "sessions.sources": "Sources ({n}):",
  "sessions.events": "Events ({n})",
  "sessions.selectHint": "Select a session from the sidebar or paste a session_id.",
  "sessions.meta.id": "Session ID",
  "sessions.meta.created": "Created",
  "sessions.meta.updated": "Updated",
  "sessions.meta.duration": "Duration",
  "sessions.meta.locale": "Locale",
  "sessions.meta.country": "Country",
  "sessions.meta.device": "Device",
  "sessions.meta.mode": "Entry mode",
  "sessions.meta.startedModes": "Mode journey",
  "sessions.meta.turns": "Turns",
  "sessions.meta.questions": "Questions",
  "sessions.meta.isTest": "Test session",
  "sessions.meta.title": "Title",
  "sessions.tags.positive": "{n} positive ratings",
  "sessions.tags.negative": "{n} negative ratings",
  "sessions.tags.flagged": "{n} turn flagged",
  "sessions.tags.flaggedPlural": "{n} turns flagged",
  "sessions.meta.userHash": "User hash",
  "sessions.meta.userHashLookup": "All sessions of this login",
  "sessions.erasure.lookupTitle": "Sessions of a login",
  "sessions.erasure.lookupHelp":
    "For an erasure or access request from a signed-in person: their name or their user hash. The user hash is a one-way hash of the name; the name is only hashed and stored nowhere. Only sessions with a login are found; an anonymous session cannot be linked to a person. People with the same name share one user hash, so the list may contain other people's sessions. For an access request, hand out only the sessions the person identifies by date and content; if they cannot, only erasure is possible.",
  "sessions.erasure.lookupLabel": "Name or user hash",
  "sessions.erasure.lookupPlaceholder": "First and last name or 64-character hash",
  "sessions.erasure.lookupRun": "Find sessions",
  "sessions.erasure.none": "No session with this login.",
  "sessions.erasure.hash": "User hash: {hash}",
  "sessions.erasure.col.role": "Role",
  "sessions.erasure.col.preview": "First question",
  "sessions.erasure.deleteTitle": "Delete session",
  "sessions.erasure.deleteHelp":
    "Permanently deletes this one session with all its turns and events. A deletion record (who, when, reason, counts) stays for 365 days. Other sessions of the same login remain and are deleted one by one.",
  "sessions.erasure.reason": "Reason, e.g. erasure request by e-mail of 20.10.2026",
  "sessions.erasure.confirm": "Type “{text}” to confirm",
  "sessions.erasure.delete": "Delete session permanently",
  "sessions.erasure.done": "Session {id}… deleted: {turns} turns, {events} events.",
  "sessions.erasure.nothing": "Nothing was stored under {id}….",
  "sessions.erasure.goneHint":
    "The session is no longer stored (deleted or expired). An erasure that stopped after the document can be repeated here: it removes what is left of the turns and events.",
  "sessions.erasure.failed_document":
    "Deletion failed: the session could not be deleted, nothing was removed. Please delete again.",
  "sessions.erasure.failed_purge":
    "Deletion incomplete: the session is deleted, {turns} turns and {events} events removed; the rest is still there. Please delete again.",

  // --- CSV export of one session -------------------------------------------
  "export.col.nr": "No.",
  "export.col.time": "Time",
  "export.col.mode": "Mode",
  "export.col.model": "Model",
  "export.col.provider": "Provider",
  "export.col.depth": "Reasoning depth",
  "export.col.question": "Question",
  "export.col.answer": "Answer",
  "export.col.sources": "Sources",
  "export.col.feedback": "Feedback",
  "export.col.feedbackComment": "Feedback comment",
  "export.col.intent": "Intent",
  "export.col.berufsfeld": "Occupational field",
  "export.col.mentions": "Mentions",
  "export.col.affect": "Affect",
  "export.col.traits": "Traits",
  "export.col.inputTokens": "Input tokens",
  "export.col.outputTokens": "Output tokens",
  "export.col.cacheHit": "Cache hit",
  "export.col.guardrail": "Guardrail",
  "export.col.streamOk": "Stream OK",
  "export.col.hits": "Hits (retrieved/used)",
  "export.col.maxRelevance": "Max relevance",
  "export.col.docsFound": "Docs found",
  "export.col.pagesForwarded": "Pages forwarded",
  "export.col.sourcesCited": "Sources cited",
  "export.col.belowFloor": "Below relevance floor",
  "export.col.contextChars": "Context chars",
  "export.col.retrievalTopic": "Retrieval topic",
  "export.col.searchQuery": "Search query (Vertex)",
  "export.col.ttft": "Time to first token (ms)",
  "export.col.latency": "Answer duration (ms)",
  "export.col.turnId": "Turn ID",

  // --- Events stream + metadata summaries ----------------------------------
  "events.filter": "Filter:",
  "events.allTypes": "(all types)",
  "events.title": "Recent analytics events ({n})",
  "events.none": "No events match this filter.",
  "eventmeta.source": "Source",
  "eventmeta.profession": "Profession",
  "eventmeta.hits": "{n} hits · max score {score}",
  "eventmeta.offersWithCommute": "{n} apprenticeships with transit time",
  "eventmeta.sourceSanitized": "Source sanitized",
  "eventmeta.withReason": "with reason",
  "eventmeta.withoutReason": "without reason",
  "eventmeta.turn": "Turn",

  // --- System prompts ------------------------------------------------------
  "prompts.version": "Prompt version",
  "prompts.disabled": "disabled",
  "prompts.model": "Model",
  "prompts.chars": "{n} characters",
  "prompts.preamble": "Preamble",
  "prompts.modelDefault": "model default",
  "prompts.adaptive": "adaptive",
  "prompts.thinking": "Thinking",
  "prompts.effort": "Effort",
  "prompts.copy": "Copy prompt",
  "prompts.copied": "Copied.",
  "prompts.copyFailed": "Copying failed: select the text and copy it by hand.",

  // --- Config --------------------------------------------------------------
  "config.edge": "Edge protection (Cloud Armor geo block)",
  "config.edgeNotReported": "The backend does not report the edge policy yet.",
  "config.edgeNotConfigured":
    "No Cloud Armor policy passed to this instance (local development). In prod, Terraform attaches the DACH geo block to the load balancer.",
  "config.row.policy": "Security policy",
  "config.row.allowedEdge": "Allowed countries (edge)",
  "config.row.everythingElse": "Everything else",
  "config.blockedAtEdge": "blocked at the edge (HTTP 403), never reaches the app",
  "config.row.rateLimit": "Per-IP rate limit",
  "config.rateLimitOn": "on · {n} requests/min, widget files excluded, then 10 min ban",
  "config.row.botProbe": "Bot-probe blocking",
  "config.botProbeOn": "on · scanner paths + UAs denied",
  "config.row.blockedSee": "Blocked visitors see",
  "config.blockedSeeNotice":
    "the widget's region notice (static assets served worldwide, API still blocked)",
  "config.blockedSeeNothing": "nothing (widget assets blocked too, no bubble appears)",
  "config.row.appBackstop": "App backstop (allowed_countries)",
  "config.row.listsMatch": "Edge and app lists match",
  "config.modes": "Model per mode",
  "config.col.mode": "Mode",
  "config.col.model": "Model",
  "config.col.provider": "Provider",
  "config.col.depth": "Reasoning depth",
  "config.col.enabled": "Enabled",
  "config.flags": "Feature flags",
  "config.flag.adminEnabled": "Admin enabled",
  "config.flag.adminToken": "Admin token set",
  "config.flag.multicheck": "Multicheck® enabled",
  "config.flag.modelArmor": "Model Armor enabled",
  "config.flag.reranker": "Reranker enabled",
  "config.flag.tracing": "Tracing enabled",
  "config.flag.tracingRate": "Tracing sample rate",
  "config.flag.metrics": "Metrics enabled",
  "config.flag.testKeyword": "Test-session keyword set",
  "config.limits": "Resource pins & limits",
  "config.limit.project": "GCP project",
  "config.limit.region": "GCP region",
  "config.limit.firestore": "Firestore database",
  "config.limit.promptVersion": "Prompt version",
  "config.limit.allowedCountries": "Allowed countries",
  "config.limit.anonLimit": "Anonymous question limit",
  "config.limit.rateSession": "Rate limit / session",
  "config.limit.rateIp": "Rate limit / IP",
  "config.limit.historyTurns": "History max turns",
  "config.limit.historyTokens": "History soft max tokens",
  "config.limit.ttl": "Anonymous TTL (days)",
  "config.knobs": "Dashboard settings",
  "config.knob.recent": "Recent sessions limit",
  "config.knob.maxWindow": "Max window (days)",

  // --- Operations (runtime switches, budgets, reset) -----------------------
  "ops.reason": "Reason for the change",
  "ops.reason.placeholder": "Why is this changing? Logged with every change.",
  "ops.reason.missing": "Nothing can be saved without a reason (at least 10 characters).",
  "ops.saving":
    "Saving… A model or depth change is probed at the provider first, which takes up to a minute.",
  "ops.saved": "Saved (version {version}). Reaches every page within about a minute.",
  "ops.lastChange": "Last change {when} by {who}",
  "ops.chat": "Chat on / off",
  "ops.chat.on": "The chat is on",
  "ops.chat.paused": "The chat is paused",
  "ops.chat.pausedBySwitch":
    "Paused by hand: no chat button on any page; new questions are refused without a model call.",
  "ops.chat.pausedByBudget":
    "The daily budget is spent: the chat pauses until midnight (Zurich) or until the daily budget is raised.",
  "ops.chat.pause": "Pause the chat",
  "ops.chat.resume": "Switch the chat back on",
  "ops.chat.raiseBudget": "Change the daily budget",
  "ops.chat.pausePermanently": "Pause permanently",
  "ops.chat.pausePermanentlyHint":
    "«Pause permanently» keeps the chat off past midnight or the quiet hours too, for maintenance say, until someone switches it back on.",
  "ops.chat.pausedBySchedule":
    "Quiet hours {start}–{end} (Zurich): the chat pauses until {end} and then runs again by itself.",
  "ops.chat.changeQuietHours": "Change the quiet hours",
  "ops.quiet": "Quiet hours",
  "ops.quiet.current": "Paused every day from {start} to {end} (Zurich).",
  "ops.quiet.none": "No quiet hours: the chat runs around the clock.",
  "ops.quiet.from": "From",
  "ops.quiet.to": "To",
  "ops.quiet.save": "Save quiet hours",
  "ops.quiet.remove": "Remove quiet hours",
  "ops.chat.budgetAlsoSpent":
    "The daily budget is spent as well: switched back on, the chat stays paused until midnight (Zurich) or until the daily budget is raised.",
  "ops.notice.close": "Close the message",
  "ops.spend": "Model cost today",
  "ops.spend.ofBudget": "of the {budget} daily budget ({percent})",
  "ops.spend.noBudget": "No daily budget set",
  "ops.spend.turns": "{n} turns",
  "ops.spend.warn": "Over 80 % of the daily budget spent.",
  "ops.spend.reached": "Daily budget reached: the chat is paused.",
  "ops.spend.days": "Last 30 days: {total}",
  "ops.budgets": "Budgets",
  "ops.budget.anonymous": "Anonymous, per session",
  "ops.budget.signedIn": "Signed in, per person and day",
  "ops.budget.daily": "Everyone together, per day",
  "ops.budget.col.budget": "Budget",
  "ops.budget.col.baseline": "Terraform",
  "ops.budget.col.effective": "In effect",
  "ops.budget.col.new": "New value",
  "ops.budget.placeholder": "empty = Terraform",
  "ops.budget.off": "off",
  "ops.budget.invalid": "Amounts of 0 or more only, for example 250 or 1'000,50.",
  "ops.budget.note":
    "Amounts in USD, shown as CHF on the dashboard (USD ≈ CHF). 0 switches a budget off.",
  "ops.budget.save": "Save budgets",
  "ops.modes": "Modes",
  "ops.mode.col.state": "State",
  "ops.mode.col.effective": "All sessions",
  "ops.mode.col.test": "Test sessions",
  "ops.mode.col.baseline": "Terraform",
  "ops.mode.unreleased": "not released",
  "ops.mode.edit": "Change",
  "ops.mode.cancel": "Cancel",
  "ops.mode.enabled": "Mode on",
  "ops.mode.model": "Model",
  "ops.mode.model.baseline": "Terraform ({model})",
  "ops.mode.depth": "Reasoning depth",
  "ops.mode.depth.baseline": "Terraform ({depth})",
  "ops.mode.scope": "Model and depth for",
  "ops.mode.scope.all": "all sessions",
  "ops.mode.scope.test": "test sessions only",
  "ops.mode.save": "Probe and save",
  "ops.mode.reset": "Back to Terraform",
  "ops.modelDefault": "model default ({depth})",
  "ops.log": "Change log",
  "ops.log.empty": "No change yet.",
  "ops.log.col.version": "Version",
  "ops.log.col.at": "Time",
  "ops.log.col.actor": "Who",
  "ops.log.col.reason": "Reason",
  "ops.log.col.change": "Change",
  "ops.wipe": "Danger zone: production-day reset",
  "ops.wipe.counts":
    "Would delete now: {sessions} sessions, {turns} turns, {events} events, {aggregates} weekly aggregates.",
  "ops.wipe.unavailable":
    "Not set up: the reset needs the password hash (CHATBOT_ADMIN_WIPE_PASSWORD_HASH).",
  "ops.wipe.confirm": "Type the project id «{text}» to confirm",
  "ops.wipe.password": "Reset password",
  "ops.wipe.submit": "Delete every conversation and statistic for good",
  "ops.wipe.busy": "Deleting…",
  "ops.wipe.done":
    "Deleted: {sessions} sessions, {turns} turns, {events} events, {aggregates} weekly aggregates. Cut-off {at}.",
  "ops.error.conflict":
    "Someone else saved in the meantime. The view has been reloaded: check the change and save again.",
  "ops.error.invalid": "This setting cannot run: {problems}",
  "ops.error.probe": "The probe at the provider failed, nothing was saved ({kind}): {detail}",
  "ops.error.unreadable":
    "The settings cannot be read right now (Firestore). Nothing was saved.",
  "ops.error.password":
    "Wrong reset password. After 5 failed attempts the reset locks for 15 minutes.",
  "ops.error.confirm": "The project id does not match.",
  "ops.error.locked": "Too many wrong passwords: the reset is locked for 15 minutes.",
  "ops.error.wipeDisabled": "The reset is not set up (no password hash).",
  "ops.error.wipeIncomplete":
    "The reset was interrupted and part is already deleted. Run it again: it picks up where it stopped.",
  "ops.error.customer": "The customer cannot be saved like this: {problems}",
  "ops.error.unknownCustomer": "This customer no longer exists. Reload the view.",
  "ops.error.quietHours": "Quiet hours need a start and an end, and the two must differ.",
  "ops.probe.not_served": "model not served in the region",
  "ops.probe.auth": "not authorised",
  "ops.probe.quota": "quota exhausted",
  "ops.probe.rejected": "request rejected",
  "ops.probe.timeout": "timed out",
  "ops.probe.unexpected": "unexpected error",

  // --- Customers & domains -------------------------------------------------
  "customers.saving": "Saving…",
  "customers.list": "Customers",
  "customers.empty": "No customer added in the dashboard yet.",
  "customers.col.name": "Name",
  "customers.col.id": "ID in the partner report",
  "customers.col.origins": "Domains",
  "customers.edit": "Edit",
  "customers.remove": "Remove",
  "customers.remove.confirm":
    "Really remove {name}? Within a few minutes the widget can no longer be opened on these pages.",
  "customers.remove.yes": "Yes, remove",
  "customers.remove.no": "Cancel",
  "customers.form.add": "Add a customer",
  "customers.form.edit": "Edit {name}",
  "customers.form.name": "Name",
  "customers.form.origins": "Domains, one per line",
  "customers.form.originsHint":
    "https://www.partner.ch for exactly that site, https://*.partner.ch for every subdomain (not the main domain itself). No path, no port.",
  "customers.form.id": "ID in the partner report: {id}",
  "customers.form.idMissing": "This name gives no ID: use letters or digits.",
  "customers.form.idTaken":
    "The ID «{id}» is taken. Edit the existing customer or choose another name.",
  "customers.form.save": "Save",
  "customers.form.cancel": "Cancel",
  "customers.terraform": "Set by Terraform",
  "customers.terraform.origins": "May embed",
  "customers.terraform.partners": "Partners in the report",
  "customers.terraform.noPartners": "No partner mapping configured.",

  // --- Analysis chat -------------------------------------------------------
  "analyst.title": "Analysis chat",
  "analyst.question": "Question to the analysis chat",
  "analyst.placeholder":
    "Ask about patterns, gaps, sources or cost in the chosen window… (Enter asks, Shift+Enter adds a line)",
  "analyst.send": "Ask",
  "analyst.new": "New conversation",
  "analyst.busy": "Analysing the data pack… this takes up to a minute.",
  "analyst.basis":
    "Based on {turns} turns in the {window} window, {excerpts} excerpts, partner: {tenant} · cost {cost}",
  "analyst.allPartners": "all",
  "analyst.refs": "Conversations cited:",
  "analyst.open": "Open in the Session Inspector",
  "analyst.error":
    "The analysis failed ({kind}). Ask again; if it timed out, choose a shorter window.",
  "analyst.kind.empty": "no answer received",
  "analyst.starter.topics": "What do people ask about most, and where is content missing?",
  "analyst.starter.sources":
    "Which sources match only weakly as the best hit, and what would improve them?",
  "analyst.starter.negative": "What stands out in the answers rated negatively?",
  "analyst.starter.cost": "Which mode costs most per conversation, and is it worth it?",
  "analyst.placeholderCheck":
    "Ask about the results of this question check… (Enter asks, Shift+Enter adds a line)",
  "analyst.basisCheck": "Based on the question check, {excerpts} excerpts · cost {cost}",
  "analyst.check.scope": "Data pack: the question check «{name}» ({date}, questions: {n}).",
  "analyst.check.back": "back to the chat data",
  "analyst.starter.check.noPage":
    "Which questions found no matching page, and which page is missing for them?",
  "analyst.starter.check.spread":
    "In which groups do the questions spread over many different best pages?",
  "analyst.starter.check.weak": "Which weak hits stand out, and what would improve the page?",

  // --- Daily report ----------------------------------------------------------
  "insights.title": "Daily report",
  "insights.partial": "preliminary",
  "insights.createdBy.scheduler": "Schedule",
  "insights.run.title": "Build a report",
  "insights.run.day": "Day",
  "insights.run.submit": "Build the report",
  "insights.run.today":
    "Today is still running: the report is preliminary, without a comparison with the days before.",
  "insights.run.replaces": "There is already a report for {day}: the new one replaces it.",
  "insights.run.outOfRange": "Choose a day from {from} to today ({to}).",
  "insights.run.busy":
    "Building the report for {day}; this takes up to about three minutes. Meanwhile no second one can start.",
  "insights.run.elapsed": "Running for {elapsed}.",
  "insights.list.title": "Stored reports",
  "insights.list.none":
    "No report stored yet. The first one is made at 03:00 at night or with «Build the report».",
  "insights.list.col.day": "Day",
  "insights.list.col.conversations": "Conversations",
  "insights.list.col.questions": "Questions",
  "insights.list.col.narrative": "Text",
  "insights.list.col.created": "Created",
  "insights.list.withText": "with text",
  "insights.list.figuresOnly": "figures only",
  "insights.list.created": "{when} · {by}",
  "insights.report.title": "Report of {day}",
  "insights.report.meta": "Created on {when} by {by}",
  "insights.report.compare": "The trends compare with the mean of the {days} days before.",
  "insights.report.noCompare":
    "Preliminary: made while the day was running, so without a comparison with the days before.",
  "insights.report.incomplete":
    "More data than the read limit: this day's figures are incomplete.",
  "insights.narrative.title": "Report",
  "insights.narrative.meta": "Written by {model} · cost {cost}",
  "insights.failure.no_conversations": "No text: there were no conversations on this day.",
  "insights.failure.empty":
    "No text: the model returned an empty answer. A new run tries again.",
  "insights.failure.timeout": "No text: the model did not answer in time. A new run tries again.",
  "insights.failure.quota":
    "No text: the model provider's quota was exhausted. Build the report again later.",
  "insights.failure.auth":
    "No text: the permission at the model provider was missing. Check the backend's configuration.",
  "insights.failure.not_served":
    "No text: the model is not served in the region. Check the analysis chat's model.",
  "insights.failure.rejected":
    "No text: the model provider rejected the request. Check the analysis chat's model.",
  "insights.failure.unexpected":
    "No text: an unexpected error occurred while writing. A new run tries again.",
  "insights.failure.other": "No text ({kind}).",
  "insights.day.title": "The day in figures",
  "insights.totals.conversations": "Conversations",
  "insights.totals.questions": "Questions",
  "insights.totals.noAnswer": "«No answer» events",
  "insights.modes.title": "Per mode",
  "insights.col.mode": "Mode",
  "insights.col.conversations": "Conversations",
  "insights.col.questions": "Questions",
  "insights.col.replies": "Replies to the chatbot's questions",
  "insights.col.ratings": "Ratings",
  "insights.col.oneQuestion": "One-question conversations",
  "insights.col.noPage": "No matching page",
  "insights.col.weakMatch": "Weak hit",
  "insights.terms.title": "Terms asked about",
  "insights.terms.baseline": "Mean/day of the {days} days before",
  "insights.terms.none": "No terms on this day.",
  "insights.modeDetail.title": "What was asked, per mode",
  "insights.unit.questions": "Questions",
  "insights.unit.conversations": "Conversations",
  "insights.unit.messages": "Messages",
  "insights.none": "none",
  "insights.error.invalid_day":
    "This day cannot be built: only today or one of the {days} days before.",
  "insights.error.storage_failed":
    "The report was built but could not be stored (Firestore). Build it again, ideally in a few minutes.",
  "insights.error.not_found":
    "This report does not exist (any more). «Build the report» for this day makes it again.",
  "insights.error.network": "No connection to the backend. Try again.",

  // --- Question check ------------------------------------------------------
  "qc.unnamed": "(unnamed)",
  "qc.paste.title": "Paste a list",
  "qc.paste.name": "Name (optional)",
  "qc.paste.text": "Search queries: copied from Excel, or one question per line",
  "qc.paste.placeholder":
    "Open the Search Console «Queries» export in Excel, copy the table and paste it here, or write one question per line.",
  "qc.paste.group": "Group",
  "qc.paste.busy":
    "Grouping… What the rules leave, a model places; that mostly takes under a minute, and up to three minutes with very many unplaced rows. The view stays usable.",
  "qc.paste.tooLong": "Too long: at most {max} characters per list.",
  "qc.groups.title": "Groups",
  "qc.groups.listMeta": "«{name}» of {date} · questions: {rows} · groups: {groups}",
  "qc.groups.stats":
    "Placed by the rules: {rules} · by the model: {model} · not placed: {unplaced} · duplicates merged: {duplicates}",
  "qc.groups.modelFailed":
    "The model could not be reached: what the rules could not place is under «Not placed».",
  "qc.groups.close": "Close the list",
  "qc.groups.mode": "Group the questions",
  "qc.groups.byKeyword": "by keyword",
  "qc.groups.byType": "by question type",
  "qc.groups.col.select": "Selection",
  "qc.groups.col.group": "Group",
  "qc.groups.col.kind": "Kind",
  "qc.groups.col.questions": "Questions",
  "qc.groups.col.clicks": "Clicks",
  "qc.groups.col.impressions": "Impressions",
  "qc.groups.col.ctr": "CTR",
  "qc.groups.col.position": "Avg position",
  "qc.groups.selectGroup": "Tick the group «{label}»",
  "qc.groups.selectQuestion": "Tick the question «{query}»",
  "qc.kind.beruf": "Profession",
  "qc.kind.begriff": "Term",
  "qc.kind.thema": "Topic",
  "qc.kind.kein_bildungsthema": "Not about education",
  "qc.kind.unvollstaendig": "Incomplete",
  "qc.kind.nicht_zugeordnet": "Not placed",
  "qc.topic.berufswahl": "Choosing a career",
  "qc.topic.bewerbung": "Applying",
  "qc.topic.lehrstellensuche": "Finding an apprenticeship",
  "qc.topic.ueberblick": "Overview",
  "qc.topic.zukunft": "Future",
  "qc.topic.lohn": "Pay",
  "qc.topic.dauer": "Duration",
  "qc.topic.nach_der_lehre": "After the apprenticeship",
  "qc.qtype.was_ist": "What is …?",
  "qc.qtype.berufsbild": "Profession overview",
  "qc.qtype.alltag": "Daily work",
  "qc.qtype.lohn": "Pay",
  "qc.qtype.dauer": "Duration",
  "qc.qtype.voraussetzungen": "Requirements",
  "qc.qtype.berufswahl": "Choosing a career",
  "qc.qtype.weiterbildung": "Further education",
  "qc.qtype.bewerbung": "Applying",
  "qc.qtype.lehrstelle": "Apprenticeship position",
  "qc.qtype.ort": "Place",
  "qc.qtype.vergleich": "Comparison",
  "qc.qtype.anmeldung": "Registration",
  "qc.qtype.stichwort": "Just a keyword",
  "qc.qtype.sonstige": "Other",
  "qc.verdict.error": "Error",
  "qc.verdict.no_page": "No matching page",
  "qc.verdict.support": "Referred to customer service",
  "qc.verdict.weak": "Weak hit",
  "qc.verdict.uncited": "No source cited",
  "qc.verdict.answered": "Answered",
  "qc.flag.listings": "Apprenticeship listings answer",
  "qc.bar.label": "Selection",
  "qc.bar.none": "Tick groups or questions to check them.",
  "qc.bar.countOne": "1 question",
  "qc.bar.count": "{n} questions",
  "qc.bar.cost": "≈ {cost}",
  "qc.bar.duration": "about {d}",
  "qc.bar.estimating": "Estimating cost and time…",
  "qc.bar.estimateFailed": "No estimate: {error}",
  "qc.bar.estimateRetry": "Estimate again",
  "qc.bar.basis":
    "Estimated from the last {turns} first FAQ answers ({model}), plus {search} search per question.",
  "qc.bar.basisAssumed":
    "An assumption: there are not yet 10 first FAQ answers to measure ({model}). Plus {search} search per question.",
  "qc.bar.note":
    "The questions run as test sessions: the statistics show them only with «Include test sessions»; the daily budget and the per-IP limit do not count them.",
  "qc.bar.tooMany": "At most {max} questions per check: untick {over}.",
  "qc.bar.page": "From which page",
  "qc.bar.pageOption": "{origin} · {sessions} sessions",
  "qc.bar.pageHint":
    "The questions run as if they came from this embedding page, with its sources and feeds. The pages with the most sessions first.",
  "qc.bar.check": "Check",
  "qc.bar.starting": "Starting…",
  "qc.bar.busy": "A check is running: a new one can start once it is paused or done.",
  "qc.run.title": "Check",
  "qc.run.meta": "«{name}» of {date} · by {by} · page: {page}",
  "qc.run.noPage": "none",
  "qc.run.progress": "{done} of {total} checked, {failed} failed",
  "qc.run.cost": "Cost so far: {cost}",
  "qc.run.pause": "Pause",
  "qc.run.resume": "Continue",
  "qc.run.close": "Close the check",
  "qc.run.running":
    "Running. Other views can be opened: the check goes on as long as this browser window stays open.",
  "qc.run.pausing": "Stopping after the step in progress…",
  "qc.run.paused": "Paused. «Continue» picks up where it stopped.",
  "qc.run.done": "Done: every question is checked.",
  "qc.stop.chat_paused":
    "Stopped: the chat is paused (switch, quiet hours or daily budget under Operations › Switches & budgets). «Continue» once it runs again.",
  "qc.stop.mode_disabled":
    "Stopped: the FAQ mode is switched off (Operations › Switches & budgets). «Continue» once it is on again.",
  "qc.stop.storage_failed":
    "Paused: the backend could not store a result; the step's other results are stored. «Continue» carries the check on, best in a few minutes.",
  "qc.stop.network":
    "Paused: the backend did not answer, even after several tries. «Continue» tries again.",
  "qc.stop.not_found": "This check no longer exists.",
  "qc.stop.failed": "Stopped ({error}). «Continue» tries again.",
  "qc.stop.stalled":
    "Paused: no question has finished for {minutes} minutes, although the backend answers. «Continue» tries again.",
  "qc.error.chat_paused":
    "The chat is paused (Operations › Switches & budgets): a check starts only once it runs again.",
  "qc.error.mode_disabled":
    "The FAQ mode is switched off (Operations › Switches & budgets): a check starts only once it is on again.",
  "qc.error.invalid_rows":
    "This selection cannot be checked: tick 1 to {max} questions of this list.",
  "qc.error.invalid_page":
    "A check cannot run from this page: the backend does not list it as an embedding page. Choose another page.",
  "qc.error.not_found": "This list or check no longer exists.",
  "qc.error.empty":
    "The list holds no question: paste the table from Excel, or one question per line.",
  "qc.error.too_many_rows":
    "Too many rows: at most {max} per list. Split the list and paste the parts one after the other.",
  "qc.error.network": "No connection to the backend. Try again.",
  "qc.results.title": "Results",
  "qc.results.none": "No results yet: the first ones appear after the first step.",
  "qc.results.byGroup": "Per group",
  "qc.results.byQuestion": "Per question, problems first",
  "qc.results.col.group": "Group",
  "qc.results.col.questions": "Questions",
  "qc.results.col.pages": "Different best pages",
  "qc.results.col.question": "Question",
  "qc.results.col.verdict": "Result",
  "qc.results.col.bestPage": "Best page",
  "qc.results.col.answer": "Answer",
  "qc.results.col.cost": "Cost",
  "qc.results.col.session": "Session",
  "qc.results.score": "Score {score}",
  "qc.results.noAnswer": "no answer stored",
  "qc.resultError.TimeoutError": "Timeout: the question got no answer within its time budget.",
  "qc.resultError.no_turn": "No turn: the chat ended the answer without storing a turn.",
  "qc.resultError.turn_not_found": "The answer's turn could no longer be found.",
  "qc.resultError.test_session_unmarked":
    "Not asked: the session could not be marked as a test session.",
  "qc.resultError.model_refusal": "The model provider's safety filter refused the answer.",
  "qc.resultError.http": "The chat answered with HTTP {status}.",
  "qc.resultError.timeout": "Model timeout: the answer never came.",
  "qc.resultError.llm_error": "The model answered with an error.",
  "qc.resultError.service_unavailable":
    "The chat was briefly unavailable (storage or infrastructure).",
  "qc.resultError.chat_paused": "The chat was paused.",
  "qc.resultError.mode_disabled": "The FAQ mode was switched off.",
  "qc.resultError.unknown": "The chat reported an error without a code.",
  "qc.results.inspector": "In the Session Inspector",
  "qc.results.export": "⬇ CSV",
  "qc.results.exportTitle": "Download the results as a CSV for Excel",
  "qc.results.discuss": "Discuss in the analysis chat",
  "qc.history.title": "Earlier lists and checks",
  "qc.history.lists": "Earlier lists",
  "qc.history.checks": "Earlier checks",
  "qc.history.noLists": "No list stored yet.",
  "qc.history.noChecks": "No check yet.",
  "qc.history.col.name": "Name",
  "qc.history.col.created": "Created",
  "qc.history.col.by": "By",
  "qc.history.col.rows": "Questions",
  "qc.history.col.groups": "Groups",
  "qc.history.col.page": "Page",
  "qc.history.col.progress": "Progress",
  "qc.history.col.cost": "Cost",
  "qc.history.status.running": "not finished",
  "qc.history.status.done": "done",
  "qc.history.open": "Open",
  "qc.history.delete": "Delete",
  "qc.history.confirmList": "Delete the list «{name}» for good?",
  "qc.history.confirmCheck": "Delete the check «{name}» of {date} with its results for good?",
  "qc.history.yes": "Yes, delete",
  "qc.history.no": "Cancel",
  "qc.history.busy": "A check is running: pause it, then another one can be opened.",
  "qc.export.meta.check": "Check",
  "qc.export.meta.created": "Created",
  "qc.export.meta.by": "By",
  "qc.export.meta.page": "Page",
  "qc.export.meta.progress": "Progress",
  "qc.export.meta.cost": "Cost USD",
  "qc.export.meta.basis": "Basis",
  "qc.export.meta.basisNote":
    "Test sessions through the FAQ: in the statistics only with «Include test sessions», not in the daily budget or the per-IP limit. Cost in USD, priced as in the dashboard.",
  "qc.export.col.group": "Group",
  "qc.export.col.kind": "Kind",
  "qc.export.col.qtype": "Question type",
  "qc.export.col.clicks": "Clicks",
  "qc.export.col.impressions": "Impressions",
  "qc.export.col.verdict": "Result",
  "qc.export.col.listings": "Apprenticeship listings answer",
  "qc.export.col.bestUrl": "Best page",
  "qc.export.col.bestTitle": "Title of the best page",
  "qc.export.col.score": "Score",
  "qc.export.col.costUsd": "Cost USD",
  "qc.export.col.error": "Error",

  // --- Test page -----------------------------------------------------------
  "testpage.embed": "Embed",
  "testpage.presets": "Presets:",
  "testpage.preset.gateway": "gateway.one: bubble with a mode menu",
  "testpage.preset.profession": "Profession page: profession in the page",
  "testpage.preset.partner": "Partner: own colour and brand",
  "testpage.preset.search": "Home page: search bar",
  "testpage.variant": "Kind",
  "testpage.variant.bubble": "Bubble (loader.js)",
  "testpage.variant.inline": "In the page (embed.js)",
  "testpage.variant.search": "Search bar (search.js)",
  "testpage.mode": "Mode",
  "testpage.profession": "Profession",
  "testpage.profession.none": "The person picks it in the chat",
  "testpage.profession.id": "Profession id",
  "testpage.profession.name": "Profession name",
  "testpage.menu": "Modes in the menu",
  "testpage.lang": "Language",
  "testpage.theme": "Appearance",
  "testpage.theme.auto": "as the device",
  "testpage.theme.light": "light",
  "testpage.theme.dark": "dark",
  "testpage.position": "Position",
  "testpage.position.right": "bottom right",
  "testpage.position.left": "bottom left",
  "testpage.color": "Main colour",
  "testpage.brand": "Brand name",
  "testpage.logo": "Logo URL (https or data:image, at most 4 KB)",
  "testpage.greeting": "Greeting",
  "testpage.starters": "Starter questions, one per line",
  "testpage.q": "First question the chat asks by itself",
  "testpage.examples": "Example questions of the search bar, one per line",
  "testpage.placeholder": "Placeholder of the search bar",
  "testpage.preview": "Preview",
  "testpage.preview.desktop": "Desktop",
  "testpage.preview.mobile": "Phone",
  "testpage.preview.reload": "Reload",
  "testpage.preview.frame": "Preview of the embed",
  "testpage.preview.title": "Example page",
  "testpage.preview.intro": "This is how the widget appears on a page that embeds it.",
  "testpage.token.on": "Sessions from this preview are test sessions (token until {until}).",
  "testpage.token.off":
    "No test keyword set (CHATBOT_TEST_SESSION_KEYWORD): sessions from this preview count like real ones, in statistics and cost.",
  "testpage.token.error":
    "No test token ({error}): sessions from this preview count like real ones, in statistics and cost.",
  "testpage.code": "Code to copy",
  "testpage.copy": "Copy code",
  "testpage.copied": "Copied.",
  "testpage.copyFailed": "Copying failed: select the code and copy it by hand.",
  "testpage.model": "How this mode answers",
  "testpage.model.test": "In this preview (test sessions): {run}",
  "testpage.model.all": "For every other session: {run}",
  "testpage.model.switch": "Models under Switches & budgets",
  "testpage.guide.title": "How the search bar works",
  "testpage.guide.what":
    "search.js puts a question field on the page, where visitors would otherwise search. Whoever types a question and presses «Ask» gets the chat's answer in a window over the page, without leaving it. The chat sits where questions come up and takes no more room on the page than the field.",
  "testpage.guide.flow": "What visitors see",
  "testpage.guide.flow.examples":
    "While nobody types, the field types example questions. It pauses when clicked into, and stays still when the device is set to reduce motion.",
  "testpage.guide.flow.dialog":
    "«Ask» opens a window with the chat in the chosen mode. Whoever has not confirmed the privacy notice yet sees it first; then the question goes out as the first message.",
  "testpage.guide.flow.fresh":
    "Every search starts a new conversation. Esc or «Close» ends it, ⤢ shows the chat full screen. While the window is open the rest of the page is locked, so keyboard and screen reader stay in the chat.",
  "testpage.guide.flow.private":
    "The question travels in the address's fragment (after «#»), which the browser never sends, so it lands in no server log. The page's Google Analytics only gets event names with the mode (such as chatbot_search_opened), never any text.",
  "testpage.guide.how": "How a page gets it",
  "testpage.guide.how.allow":
    "Allow the page's domain, under Customers & domains or in Terraform. Otherwise the browser does not show the chat in the window.",
  "testpage.guide.how.paste":
    "Put the code to copy (below) where the field should stand on the page. The <div> sets its place and width, «data-target» in the script names it. A <div> always takes the field; list and table elements do not.",
  "testpage.guide.how.options":
    "Everything else goes through the script's data attributes, all settable above: «data-mode» (mode), «data-lang» (language of the field and the answers), «data-examples» (up to 10 example questions as a JSON list), «data-placeholder», «data-primary-color», «data-brand-name». On gateway.one's blue the button is orange; a partner colour becomes its own accent.",
  "testpage.guide.how.test":
    "Try it in the preview: its sessions are test sessions, the partner page starts real ones. When the chat is paused the field stays, and the chat in the window shows the pause itself.",

  // --- Glossary ------------------------------------------------------------
  "glossary.intro":
    "The dashboard's terms: what each one means and why it matters. The same terms appear as ⓘ markers and as the explanation line under each panel.",
  "glossary.term": "Term",
  "glossary.what": "What it is",
  "glossary.why": "Why it matters",
};
